<template>
  <section
    class="RunPlayerListView"
  >
    <PlayerList
      v-if="inPlayers.length > 0"
      data-testid="inList"
      iconName="check"
      title="In"
      :players="inPlayers"
      v-bind="{statusMap, subRequestMap, awardMap}"
      isActive
      includeCount
    >
      <template
        v-if="isCommish"
        #buttonList="{player, status}"
      >
        <PlayerListStatusButtonView
          v-bind="{run, player, status}"
        />
      </template>
    </PlayerList>

    <PlayerList
      v-if="maybePlayers.length > 0"
      data-testid="maybeList"
      iconName="circleHalf"
      title="Maybe"
      :players="maybePlayers"
      v-bind="{statusMap, subRequestMap, awardMap}"
      isActive
    >
      <template
        v-if="isCommish"
        #buttonList="{player, status}"
      >
        <PlayerListStatusButtonView
          v-bind="{run, player, status}"
        />
      </template>
    </PlayerList>

    <PlayerList
      v-if="isCommish && waitlistPlayers.length > 0"
      data-testid="waitingList"
      iconName="circleClock"
      title="Waitlist"
      :players="waitlistPlayers"
      v-bind="{statusMap, subRequestMap}"
      includeCount
      isActive
    >
      <template
        v-if="isCommish"
        #buttonList="{player}"
      >
        <PlayerListWaitlistButtonView
          v-bind="{run, player}"
          @update:subRequest="$emit('update:subRequest', $event)"
          @update:status="$emit('update:status', $event)"
        />
      </template>
    </PlayerList>

    <PlayerList
      v-if="pendingPlayers.length > 0"
      data-testid="pendingList"
      iconName="circleEmpty"
      title="Pending"
      :players="pendingPlayers"
      :maxCount="pendingPlayers.length > maxPlayerListCount ? Math.max(maxPlayerListCount - 1, outPlayers.length) : undefined"
      v-bind="{statusMap, subRequestMap}"
    >
      <template
        v-if="isCommish"
        #buttonList="{player, status}"
      >
        <PlayerListStatusButtonView
          v-bind="{run, player, status}"
        />
      </template>
    </PlayerList>

    <PlayerList
      v-if="outPlayers.length > 0"
      data-testid="outList"
      iconName="x"
      title="Out"
      :players="outPlayers"
      v-bind="{statusMap, subRequestMap}"
    >
      <template
        v-if="isCommish"
        #buttonList="{player, status}"
      >
        <PlayerListStatusButtonView
          v-bind="{run, player, status}"
        />
      </template>
    </PlayerList>
  </section>
</template>

<script lang="ts" setup>
import PlayerList from "@/components/PlayerList.vue";
import PlayerListStatusButtonView from "@/views/PlayerListStatusButtonView.vue";
import { RunWithAssociations } from "@shared/lib/database/queries/fetchRunWithAssociations";
import { Player, Status, SubRequest } from "@shared/lib/database/query.types";
import { computed } from "vue";
import PlayerListWaitlistButtonView from "./PlayerListWaitlistButtonView.vue";

defineEmits<{
  "update:status": [status: Pick<Status, "run_id" | "player_id" | "probability">],
  "update:subRequest": [subRequest: SubRequest],
}>();

const {
  run,
  isCommish = false,
  inPlayers = [],
  maybePlayers = [],
  outPlayers = [],
  waitlistPlayers = [],
  pendingPlayers = [],
} = defineProps<{
  run: RunWithAssociations,
  isCommish?: boolean,
  inPlayers?: Array<Player>,
  maybePlayers?: Array<Player>,
  outPlayers?: Array<Player>,
  waitlistPlayers?: Array<Player>,
  pendingPlayers?: Array<Player>,
}>();

const maxPlayerListCount = 8;

const awardMap = computed(() => {
  return new Map(run.award_winners.map((awardWinner) => {
    return [awardWinner.recipient_id, awardWinner.award];
  }));
});

const statusMap = computed(() => {
  return new Map(run.statuses.map((status) => {
    return [status.player_id, status];
  }));
});

const subRequestMap = computed(() => {
  return new Map(run.sub_requests.map((subRequest) => {
    return [subRequest.player_id, subRequest];
  }));
});
</script>

<style lang="scss" scoped>
.RunPlayerListView {
  display: grid;
  gap: 16px;
  padding: 20px 16px;
}
</style>
